import { useEffect, useMemo, useState } from 'react';
import {
  fetchAlertHistory,
  fetchDailyDocs,
  fetchDeviceStatus,
  fetchRainForecastDocs,
  fetchSensorLocation,
} from './sensorData';
import {
  alertBands,
  appendLatest,
  breakAtGaps,
  expandDailyDocs,
  expandRainForecastDocs,
  forecastSeries,
  maxByBucket,
  rainSlotBars,
  rangeForView,
} from './waterLevelQuery';

/** Same cadence as the previous sensor modal. */
const REFRESH_MS = 5 * 60 * 1000;
const EMPTY_RAIN = { slots: [], expectedSlots: 0, missingSlots: 0 };

const DEBUG = process.env.REACT_APP_DEBUG_MODE === 'true';
const debugLog = (...args) => {
  if (DEBUG) console.log('[sensor]', ...args);
};

/**
 * Loads one sensor the way the old modal did: status documents every 5 minutes,
 * plus waterLevel_daily, rainForecast_daily, and the alert history for the
 * view: alert_history (alertSource 'rule'), alert_history_rf ('rf'), or
 * nothing ('none').
 * `knownSensor` skips a second sensor_locations read when the map list
 * already has the document.
 */
export function useSensorSeries(deviceId, view, custom, knownSensor, alertSource = 'rule') {
  const [now, setNow] = useState(() => Date.now());
  const [fetchedSensor, setFetchedSensor] = useState(null);
  const [status, setStatus] = useState({
    deviceId: null, latest: null, alert: null, rfAlert: null, forecast: null,
  });
  const [history, setHistory] = useState({ key: null });
  const [alertHistory, setAlertHistory] = useState({ key: null, bands: [] });

  const viewKey = view === 'custom'
    ? `${deviceId}|custom|${custom?.from}|${custom?.to}`
    : `${deviceId}|${view}`;

  const range = useMemo(
    () => (deviceId ? rangeForView(view, { now, custom }) : null),
    [deviceId, view, now, custom]
  );
  const rangeKey = range ? `${range.startMs}|${range.endMs}|${range.xMaxMs}` : '';

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setFetchedSensor(null);
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId || knownSensor) return undefined;
    let cancelled = false;
    fetchSensorLocation(deviceId)
      .then((loc) => {
        if (!cancelled) setFetchedSensor(loc);
      })
      .catch((err) => console.error('[sensor] sensor_locations:', err));
    return () => {
      cancelled = true;
    };
  }, [deviceId, knownSensor]);

  useEffect(() => {
    if (!deviceId) return undefined;
    let cancelled = false;
    fetchDeviceStatus(deviceId)
      .then((next) => {
        if (!cancelled) setStatus({ deviceId, ...next });
      })
      .catch((err) => console.error('[sensor] status docs:', err));
    return () => {
      cancelled = true;
    };
  }, [deviceId, now]);

  useEffect(() => {
    if (!deviceId || !range) return undefined;
    let cancelled = false;
    debugLog('Loading', viewKey, new Date(range.startMs), new Date(range.xMaxMs));
    Promise.all([
      fetchDailyDocs(deviceId, range.startMs, range.endMs),
      fetchRainForecastDocs(deviceId, range.startMs, range.xMaxMs).catch((err) => {
        console.error('[sensor] rainForecast_daily:', err);
        return [];
      }),
    ])
      .then(([dailyDocs, rainDocs]) => {
        if (cancelled) return;
        debugLog(`${dailyDocs.length} daily docs, ${rainDocs.length} rain docs`);
        setHistory({
          key: viewKey,
          range,
          water: expandDailyDocs(dailyDocs, range.startMs, range.endMs),
          rain: expandRainForecastDocs(rainDocs, range.startMs, range.xMaxMs, range.nowMs),
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[sensor] history:', err);
        setHistory((prev) => (prev.key === viewKey && !prev.error ? prev : {
          key: viewKey,
          range,
          water: [],
          rain: EMPTY_RAIN,
          error: err.message || String(err),
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, range, rangeKey, viewKey]);

  // Loaded on its own so switching alertSource does not reload the water.
  const bandsKey = `${viewKey}|${alertSource}`;
  useEffect(() => {
    if (!deviceId || !range) return undefined;
    if (alertSource === 'none') {
      setAlertHistory({ key: bandsKey, bands: [] });
      return undefined;
    }
    let cancelled = false;
    fetchAlertHistory(deviceId, range.startMs, range.endMs, alertSource)
      .catch((err) => {
        console.error(`[sensor] alert history (${alertSource}):`, err);
        return [];
      })
      .then((episodes) => {
        if (cancelled) return;
        debugLog(`${episodes.length} ${alertSource} alert episodes`);
        setAlertHistory({ key: bandsKey, bands: alertBands(episodes, range.startMs, range.endMs, range.nowMs) });
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, range, rangeKey, bandsKey, alertSource]);

  const ready = history.key === viewKey && !history.error;
  const failed = history.key === viewKey && history.error;

  const series = useMemo(() => {
    if (!ready) return null;
    const sameDevice = status.deviceId === deviceId;
    const latest = sameDevice ? status.latest : null;
    const forecastDoc = sameDevice ? status.forecast : null;
    const { range: loaded, water, rain } = history;
    const bands = alertHistory.key === bandsKey ? alertHistory.bands : [];
    const forecast = loaded.showForecast ? forecastSeries(forecastDoc, loaded.nowMs) : [];
    const rainBars = rainSlotBars(rain.slots, loaded.bucketMinutes, loaded.startMs, loaded.xMaxMs);
    const waterBuckets = appendLatest(
      maxByBucket(water, loaded.bucketMinutes),
      latest,
      loaded.startMs,
      loaded.endMs,
    );
    return {
      range: loaded,
      water,
      bands,
      waterBuckets: breakAtGaps(waterBuckets, loaded.bucketMinutes),
      rain,
      rainPast: rainBars.past,
      rainUpcoming: loaded.showForecast ? rainBars.upcoming : [],
      forecast,
    };
  }, [ready, history, alertHistory, bandsKey, status, deviceId]);

  const currentStatus = status.deviceId === deviceId
    ? status
    : { latest: null, alert: null, rfAlert: null, forecast: null };

  return {
    sensor: knownSensor || fetchedSensor,
    status: currentStatus,
    series,
    ready,
    error: failed ? history.error : null,
    loading: Boolean(deviceId) && !ready && !failed && Boolean(range),
  };
}
